import { useEffect } from 'react';
import confetti from 'canvas-confetti';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Coins, PartyPopper } from 'lucide-react';

/** Thank-you moment after a contribution lands. Same celebration language as the
 *  puzzle win, because contributing deserves the same applause. */
export function ContributionThanksDialog({ open, onOpenChange, chapters = 0, coins = 100 }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const burst = (opts) => confetti({ particleCount: 70, spread: 70, origin: { y: 0.6 }, ...opts });
    burst({ colors: ['#f59e0b', '#fbbf24', '#34d399', '#60a5fa'] });
    const t1 = setTimeout(() => burst({ angle: 60, origin: { x: 0, y: 0.7 } }), 220);
    const t2 = setTimeout(() => burst({ angle: 120, origin: { x: 1, y: 0.7 } }), 400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [open]);

  const total = Math.max(1, chapters) * coins;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl p-0 overflow-hidden" data-testid="contribution-thanks-dialog">
        <div className="bg-gradient-to-br from-amber-400 via-orange-400 to-rose-400 px-6 pt-7 pb-6 text-center text-white">
          <PartyPopper className="w-9 h-9 mx-auto" />
          <DialogTitle className="font-heading text-2xl font-bold mt-2">Your upload is complete!</DialogTitle>
          <p className="font-heading text-sm mt-1">Thank you for contributing to Gurukul Veda</p>
        </div>
        <div className="px-6 py-5 space-y-3">
          <p className="text-[13px] font-body text-slate-600 leading-relaxed">
            Every chapter you contribute helps us make learning more accessible, organised and effective.
          </p>
          <p className="text-[13px] font-body text-slate-600 leading-relaxed">
            Your submission has been received and is awaiting review. Once approved you’ll receive
            <span className="font-heading text-slate-900"> {coins} Veda Coins</span> for each eligible chapter
            {chapters > 1 ? ` — up to ${total} coins for this upload` : ''}. Track it any time under
            <span className="font-heading text-slate-900"> My Veda Coins</span>.
          </p>
          <p className="text-[12px] font-body text-slate-500 italic">
            Keep contributing, keep learning and keep collecting your rewards.
            <br />One chapter at a time. One step ahead every day.
            <br /><span className="not-italic font-heading text-slate-600">— Team Gurukul Veda</span>
          </p>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" size="sm" className="flex-1 rounded-full font-heading text-xs"
              data-testid="thanks-close-btn" onClick={() => onOpenChange(false)}>
              Keep studying
            </Button>
            <Button size="sm" className="flex-1 rounded-full font-heading text-xs bg-amber-500 hover:bg-amber-600"
              data-testid="thanks-view-coins-btn"
              onClick={() => { onOpenChange(false); navigate('/my-veda-coins'); }}>
              <Coins className="w-3.5 h-3.5 mr-1" /> My Veda Coins
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ContributionThanksDialog;
